// ════════════════════════════════════════════════════════════════════
// STATUS VOCABULARY — canonical reference for badge labels + colors.
//
// Imported from the FTPIG_CRM.html reference design. Source of truth
// for any new status dropdown, filter chip, or badge tone we wire up.
//
// CASING RULE (from the reference):
//   · Appointments use UPPERCASE values.
//   · Every other record type uses lowercase.
//   Mixing casing silently breaks badge coloring.
//
// SCHEMA NOTES — where our internal stores differ:
//   · Leads in our DB use lowercase snake_case IDs ('appt_set', 'no_show',
//     'follow_up', 'interested'). The reference's appointment vocabulary is
//     UPPERCASE-with-spaces ('BOOKED', 'NO SHOW', 'QUOTED FOLLOW-UP',
//     'SHOWED'). Use LEAD_STATUS_LABEL() to translate.
//   · Policies in our DB use 'active' | 'pending' | 'cancelled' | 'lapsed'
//     | 'renewed'. The reference uses 'active' | 'expired' | 'cancelled'
//     where 'lapsed' is stored as 'expired'. Until we migrate, treat
//     'lapsed' and 'expired' as synonymous on read.
//   · Our chargebacks table uses `resolved_at` (timestamptz) not a
//     status column. To match the reference's 'active' | 'watching' |
//     'resolved', derive: resolved_at != null → 'resolved'; otherwise
//     'active' or 'watching' is a UI-only state.
// ════════════════════════════════════════════════════════════════════

// ── APPOINTMENTS ────────────────────────────────────────────────────
export const APPOINTMENT_STATUSES = [
  'QUOTED FOLLOW-UP',
  'BOOKED',
  'SHOWED',
  'CLOSED',
  'NO SHOW',
  'RESCHEDULE',
  'NOT INTERESTED',
  'NOT YET',
  'QUOTED',
];

// Anything unmapped renders as status-quoted (blue) — same fallback the
// reference uses.
export const APPOINTMENT_BADGE_CLASS = {
  'QUOTED FOLLOW-UP': 'status-followup',
  'BOOKED':           'status-booked',
  'SHOWED':           'status-showed',
  'CLOSED':           'status-closed',
  'NO SHOW':          'status-noshow',
  'RESCHEDULE':       'status-reschedule',
  'NOT INTERESTED':   'status-notinterested',
  'NOT YET':          'status-notyet',
  'QUOTED':           'status-quoted',
};

// ── LEAD ↔ APPOINTMENT mapping ──────────────────────────────────────
// Internal lead status IDs (lowercase snake_case) we save to the DB
// mapped to the reference's UPPERCASE appointment vocabulary. Lets us
// keep our data layer untouched while UI labels match the reference.
export const LEAD_STATUS_TO_APPT_LABEL = {
  'new':            'QUOTED',
  'contacted':      'QUOTED',
  'follow_up':      'QUOTED FOLLOW-UP',
  'appt_set':       'BOOKED',
  'showed':         'SHOWED',
  'interested':     'SHOWED',
  'closed':         'CLOSED',
  'closed_won':     'CLOSED',
  'closed_lost':    'NOT INTERESTED',
  'no_show':        'NO SHOW',
  'reschedule':     'RESCHEDULE',
  'not_interested': 'NOT INTERESTED',
  'dnc':            'NOT INTERESTED',
  'recycled':       'QUOTED',
};

export function leadStatusLabel(internalStatusId) {
  return LEAD_STATUS_TO_APPT_LABEL[internalStatusId] || (internalStatusId || '').toUpperCase();
}

export function leadStatusBadgeClass(internalStatusId) {
  const label = leadStatusLabel(internalStatusId);
  return APPOINTMENT_BADGE_CLASS[label] || 'status-quoted';
}

// ── COMMISSIONS ─────────────────────────────────────────────────────
export const COMMISSION_STATUSES = ['pending', 'paid', 'overdue', 'chargeback'];
export const COMMISSION_BADGE_CLASS = {
  pending:    'comm-pending',
  paid:       'comm-paid',
  overdue:    'comm-overdue',
  chargeback: 'comm-chargeback',
};

// ── POLICIES ────────────────────────────────────────────────────────
// Reference: 'active' | 'expired' | 'cancelled' (Lapsed → stored as expired).
// Our DB still uses 'lapsed' on the life side and 'pending' / 'renewed' on
// the health side. Read code should treat 'lapsed' and 'expired' as the
// same state.
export const POLICY_STATUSES_REFERENCE = ['active', 'expired', 'cancelled'];
export const POLICY_STATUSES_INTERNAL  = ['active', 'pending', 'cancelled', 'lapsed', 'expired', 'renewed'];

export const POLICY_LABEL = {
  active:    'Active',
  pending:   'Pending',
  cancelled: 'Cancelled',
  lapsed:    'Expired / Lapsed',
  expired:   'Expired / Lapsed',
  renewed:   'Renewed',
};

// Reference's color mapping borrows from the appointment badge classes.
export const POLICY_BADGE_CLASS = {
  active:    'status-closed',       // green
  cancelled: 'status-noshow',       // red
  lapsed:    'status-notyet',       // teal
  expired:   'status-notyet',       // teal
  pending:   'status-reschedule',   // amber
  renewed:   'status-showed',       // orange
};

export function policyStatusEquivalent(s) {
  // Read-side equivalence: a 'lapsed' policy is the same lifecycle
  // state as 'expired'. Use this when grouping for counts or filters
  // so the reference vocabulary lines up with our stored values.
  if (s === 'lapsed') return 'expired';
  return s;
}

// ── FOLLOW-UPS ──────────────────────────────────────────────────────
export const FOLLOWUP_STATUSES   = ['open', 'completed', 'skipped'];
export const FOLLOWUP_BADGE_CLASS = {
  open:      'status-booked',
  completed: 'status-closed',
  skipped:   'status-notinterested',
};
export const FOLLOWUP_PRIORITIES = ['high', 'med', 'low'];
export const FOLLOWUP_PRIORITY_CLASS = { high: 'prio-high', med: 'prio-med', low: 'prio-low' };

export const FOLLOWUP_TYPES = [
  'Call Back',
  'Quoted Follow-Up',
  'Reschedule',
  'Renewal Outreach',
  'Upsell / Add-On',
  'Check In',
];

// ── CHARGEBACKS ─────────────────────────────────────────────────────
export const CHARGEBACK_STATUSES = ['active', 'watching', 'resolved'];
export const CHARGEBACK_BADGE_CLASS = {
  active:   'cb-active',
  watching: 'cb-watching',
  resolved: 'cb-resolved',
};

export const CHARGEBACK_REASONS = [
  'Client cancelled',
  'Non-payment / lapse',
  'Underwriting reversal',
  'Client complained',
  'Refund requested',
  'Policy not issued',
  'Other',
];

// ── BADGE CLASS → TONE/SWATCH MAP ───────────────────────────────────
// Tailwind-friendly tone keys our existing components already accept
// (slate, teal, amber, rose, emerald, sky, violet, red, orange).
// Use HpBadgeTone(badgeClass) when wiring a chip whose component
// expects a tone key rather than a CSS class name.
export const BADGE_CLASS_TONE = {
  // Appointments
  'status-closed':         'emerald', // success / closed / active
  'status-booked':         'emerald',
  'status-showed':         'orange',
  'status-noshow':         'red',
  'status-reschedule':     'amber',
  'status-quoted':         'sky',
  'status-followup':       'violet',
  'status-notinterested':  'violet',
  'status-notyet':         'teal',
  // Commissions
  'comm-pending':          'amber',
  'comm-paid':             'emerald',
  'comm-overdue':          'red',
  'comm-chargeback':       'rose',
  // Follow-up priorities
  'prio-high':             'red',
  'prio-med':              'amber',
  'prio-low':              'slate',
  // Chargebacks
  'cb-active':             'red',
  'cb-watching':           'amber',
  'cb-resolved':           'emerald',
};

export function badgeClassTone(badgeClass) {
  return BADGE_CLASS_TONE[badgeClass] || 'slate';
}
